import { CircularProgress, Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { DrawerHeader } from "../../components/layout/Drawer/DrawerMain";
import Meta from "../../components/common/Meta";
import { FileSidebarProvider } from "../../context/FileSidebarContext";
import { useFileContext } from "../../context/FileContext";
import { uiHeight } from "../../utils/uiHeight";
import Generating from "./components/Generating";
import Sidebar from "./components/Sidebar";
import TextEditor from "./components/TextEditor/index";

interface LocationState {
  content?: string;
  title?: string;
}

function TextFile() {
  const { fileUuid: rawFileUuid } = useParams(); // 元のパラメータ名を変更
  const fileUuid = rawFileUuid === undefined ? "new" : rawFileUuid; // undefinedなら"new"として扱う
  const isNewFile = fileUuid === "new";
  const location = useLocation();
  const { setFileText, setFileTitle, clearFileData } = useFileContext();

  const [status, setStatus] = useState(isNewFile ? 0 : 3); // New files don't have a status initially
  const [loading, setLoading] = useState(!isNewFile); // Don't show loading for new files
  const [initialDataSet, setInitialDataSet] = useState(false);
  const { t } = useTranslation();

  // ナビゲーションステートから初期データを設定
  useEffect(() => {
    if (isNewFile && !initialDataSet) {
      // 新しいファイルの場合、まず既存の"new"キーのデータをクリア
      clearFileData(fileUuid);

      // location.stateから初期データを設定
      if (location.state) {
        const state = location.state as LocationState;
        if (state.content) {
          setFileText({ uuid: fileUuid, content: state.content });
        }
        if (state.title) {
          setFileTitle({ uuid: fileUuid, title: state.title });
        }
      }
      setInitialDataSet(true);
    }
  }, [isNewFile, location.state, fileUuid, setFileText, setFileTitle, clearFileData, initialDataSet]);

  const getTextFile = async () => {
    try {
      // isNewFile で判定するため、fileUuid が "new" の場合も API 呼び出ししない
      if (isNewFile) return;
      setLoading(true);
      // API呼び出しには加工前の rawFileUuid (実際のUUIDかundefined) を使うべきだが、
      // isNewFile でガードしているので、ここに来る fileUuid は実際のUUIDのはず。
      // ただし、安全のため rawFileUuid を使う方が良いかもしれない。
      // ここでは fileUuid (加工後) を使うが、もし "new" 文字列でAPIを叩く可能性があるなら修正が必要。
      // 現状のロジックでは isNewFile が true なら早期リターンするので問題ない。
      const res = await axios.get("/api/v1/text-file/" + fileUuid);
      if (res.data) {
        return res.data;
      }
    } catch (error) {
      console.error("An unknown error occurred:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTextFile = async () => {
    if (isNewFile) return; // Do not fetch if it's a new file
    const res = await getTextFile();
    if (res) {
      setStatus(res.status);
    }
  };

  // 最初のuseEffect
  useEffect(() => {
    if (!isNewFile) {
      fetchTextFile();
    }
  }, [fileUuid, isNewFile]); // fileUuid (加工後) を依存配列に

  // 生成中は5秒毎に取得
  useEffect(() => {
    if (isNewFile || status !== 3) {
      // Do not poll for new files
      return;
    }

    const timer = setInterval(() => {
      fetchTextFile();
    }, 5000);

    return () => {
      clearInterval(timer);
    };
  }, [status]);

  return (
    <>
      <Meta title={t("textEditor.title")} meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <DrawerHeader />
      <Box sx={{ borderRadius: 0, height: uiHeight() }}>
        {(!loading || isNewFile) && status !== 3 && (
          // key には加工後の fileUuid ("new" または実際のUUID) を使用
          <Box key={fileUuid} height={"100%"} px={2}>
            <Grid container spacing={2} justifyContent="center" height={"100%"}>
              <Grid
                height={"100%"}
                size={{
                  xs: 12,
                  md: 7.2,
                }}
              >
                <TextEditor />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4.8,
                }}
              >
                <FileSidebarProvider>
                  <Sidebar />
                </FileSidebarProvider>
              </Grid>
            </Grid>
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: uiHeight(),
              flexDirection: "row",
            }}
          >
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant={"h5"} component={"h2"} fontWeight={600} sx={{ mt: 2 }}>
              Loading...
            </Typography>
          </Box>
        )}
        {!loading && status === 3 && <Generating />}
      </Box>
    </>
  );
}

export default TextFile;
